import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useEffect } from 'react';

export function CookiePolicy() {
  useEffect(() => {
    document.title = 'xReplAI - Cookie Policy';
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1628] dark:bg-[#0A1628]">
      <Header />
      <main className="max-w-4xl mx-auto px-6 py-24">
        <div className="prose prose-invert max-w-none">
          <Link to="/" className="text-[#60A5FA] hover:text-[#3B82F6] mb-8 inline-block">
            ← Back to Home
          </Link>
          
          <h1 className="text-4xl font-bold text-white mb-8">Cookie Policy</h1>
          <p className="text-gray-400 mb-8">Last updated: February 4, 2026</p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">1. What Are Cookies</h2>
            <p className="text-gray-300 mb-4">
              Cookies are small text files that are placed on your computer or mobile device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">2. How We Use Cookies</h2>
            <p className="text-gray-300 mb-4">
              xRepl.ai uses cookies and similar tracking technologies to enhance your experience, analyze usage patterns, and improve our services. We use both session cookies (which expire when you close your browser) and persistent cookies (which stay on your device for a set period or until you delete them).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3">3.1 Strictly Necessary Cookies</h3>
            <p className="text-gray-300 mb-4">
              These cookies are essential for the Service to function properly. They enable core functionality such as security, authentication, and remembering your preferences. The Service cannot function properly without these cookies.
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Authentication and session management</li>
              <li>Security and fraud prevention</li>
              <li>Load balancing</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">3.2 Performance and Analytics Cookies</h3>
            <p className="text-gray-300 mb-4">
              These cookies collect information about how visitors use our Service, such as which pages are visited most often. This information helps us improve how the Service works.
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Google Analytics (usage statistics and visitor behavior)</li>
              <li>Error tracking and debugging</li>
              <li>Performance monitoring</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">3.3 Functional Cookies</h3>
            <p className="text-gray-300 mb-4">
              These cookies allow the Service to remember choices you make (such as your user preferences) and provide enhanced, personalized features.
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Theme preferences (dark/light mode)</li>
              <li>Language preferences</li>
              <li>User interface customization</li>
            </ul>

          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">4. Third-Party Cookies</h2>
            <p className="text-gray-300 mb-4">
              In addition to our own cookies, we may use third-party cookies for service functionality. These third parties include:
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li><strong>Supabase:</strong> For user authentication and session management</li>
              <li><strong>Stripe:</strong> For secure payment processing during checkout</li>
              <li><strong>Vercel:</strong> For website hosting and performance analytics</li>
            </ul>
            <p className="text-gray-300 mb-4">
              These third parties may also use cookies in accordance with their own privacy policies. We encourage you to review their policies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">5. Chrome Extension Storage</h2>
            <p className="text-gray-300 mb-4">
              As a Chrome extension, xRepl.ai uses browser local storage to save your preferences and settings. This data is stored locally on your device and is not transmitted to our servers unless necessary for service functionality.
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>User preferences and settings</li>
              <li>Authentication tokens</li>
              <li>Temporary draft content</li>
              <li>Usage statistics (optional)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">6. How to Control Cookies</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3">6.1 Browser Settings</h3>
            <p className="text-gray-300 mb-4">
              Most web browsers allow you to control cookies through their settings. You can:
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li>Delete all cookies from your browser</li>
              <li>Block all cookies from being set</li>
              <li>Allow only first-party cookies</li>
              <li>Receive a notification before a cookie is set</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3">6.2 Chrome Extension</h3>
            <p className="text-gray-300 mb-4">
              The xRepl.ai Chrome extension does not use cookies. It stores user preferences (license key, selected AI model, generation settings) using Chrome's built-in extension storage APIs (<code className="text-[#60A5FA]">chrome.storage.sync</code> and <code className="text-[#60A5FA]">chrome.storage.local</code>). You can clear this data by uninstalling the extension or clearing extension data in Chrome settings.
            </p>

            <h3 className="text-xl font-semibold text-white mb-3">6.3 Opt-Out Options</h3>
            <p className="text-gray-300 mb-4">
              You can opt out of certain types of tracking:
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li><strong>Vercel Analytics:</strong> Vercel's analytics respect browser Do Not Track settings by default</li>
            </ul>

            <p className="text-gray-300 mb-4 p-4 bg-yellow-900/20 border border-yellow-800 rounded">
              <strong>Note:</strong> Blocking cookies may impact your experience and prevent certain features from working properly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">7. Do Not Track Signals</h2>
            <p className="text-gray-300 mb-4">
              Some browsers include a "Do Not Track" (DNT) feature that signals to websites that you do not want to have your online activity tracked. Our Service currently does not respond to DNT signals due to lack of industry-wide standards.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">8. Cookie Retention</h2>
            <p className="text-gray-300 mb-4">
              Different cookies have different retention periods:
            </p>
            <ul className="text-gray-300 mb-4 list-disc pl-6">
              <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
              <li><strong>Persistent cookies:</strong> Remain for a specified period (typically 30 days to 2 years)</li>
              <li><strong>Local storage:</strong> Persists until manually cleared or the extension is uninstalled</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">9. Updates to This Policy</h2>
            <p className="text-gray-300 mb-4">
              We may update this Cookie Policy from time to time to reflect changes in our practices or for legal, operational, or regulatory reasons. We will notify you of any material changes by posting the updated policy on this page with a new "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">10. More Information</h2>
            <p className="text-gray-300 mb-4">
              For more information about how we collect, use, and protect your data, please review our <Link to="/privacy" className="text-[#60A5FA] hover:text-[#3B82F6]">Privacy Policy</Link>.
            </p>
            <p className="text-gray-300 mb-4">
              If you have questions about our use of cookies, please contact us at:
            </p>
            <p className="text-gray-300">
              Email: <a href="mailto:support@xrepl.ai" className="text-[#60A5FA] hover:text-[#3B82F6]">support@xrepl.ai</a><br />
              Website: <a href="https://www.xrepl.ai" className="text-[#60A5FA] hover:text-[#3B82F6]">www.xrepl.ai</a>
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-white mb-4">11. Cookie List</h2>
            <p className="text-gray-300 mb-4">
              Below is a detailed list of cookies we use:
            </p>
            
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-gray-300">
                <thead className="bg-blue-900/30 border-b border-blue-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-white">Cookie Name</th>
                    <th className="px-4 py-3 text-left text-white">Purpose</th>
                    <th className="px-4 py-3 text-left text-white">Duration</th>
                    <th className="px-4 py-3 text-left text-white">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-900/20">
                  <tr>
                    <td className="px-4 py-3">sb-*-auth-token</td>
                    <td className="px-4 py-3">Supabase user authentication session</td>
                    <td className="px-4 py-3">Session / 1 week</td>
                    <td className="px-4 py-3">Strictly Necessary</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">__stripe_mid</td>
                    <td className="px-4 py-3">Stripe fraud prevention during checkout</td>
                    <td className="px-4 py-3">1 year</td>
                    <td className="px-4 py-3">Strictly Necessary</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">_vercel_jwt</td>
                    <td className="px-4 py-3">Vercel deployment session</td>
                    <td className="px-4 py-3">Session</td>
                    <td className="px-4 py-3">Performance</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}